import { useEffect, useState, useCallback } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Modal } from '@/components/ui/modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DollarSign,
  ExternalLink,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  adminApi,
  AdminOrganization,
  InternalInvoice,
  ReconciliationReport,
  StripeSyncResponse,
} from '../services/adminApi';
import { CreateInvoiceDialog } from './OrgBillingTab';

// Map the Fluent Badge color names still returned by the get*Color helpers below onto shadcn
// Badge classes, preserving the status color SEMANTICS after the migration (mirrors OrgBillingTab):
// success = emerald, warning = amber, danger = red, informative/subtle = neutral gray, brand = blue.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};

function badgeClass(color: string): string {
  return BADGE_CLASS[color] || BADGE_CLASS.informative;
}

function AdminBillingTab() {
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invoice state
  const [invoices, setInvoices] = useState<InternalInvoice[]>([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>('');
  const [invoicePeriodFilter, setInvoicePeriodFilter] = useState<string>('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<StripeSyncResponse | null>(null);

  // Reconciliation state
  const [reconciling, setReconciling] = useState(false);
  const [reconReport, setReconReport] = useState<ReconciliationReport | null>(null);

  // Create invoice dialog state
  const [createInvoiceDialogOpen, setCreateInvoiceDialogOpen] = useState(false);

  // Credit dialog state
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditOrg, setCreditOrg] = useState<AdminOrganization | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditSubmitting, setCreditSubmitting] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);
  const [creditSuccess, setCreditSuccess] = useState<string | null>(null);

  // Platform API key from env (for invoice admin endpoints)
  const platformApiKey = (import.meta.env.VITE_PLATFORM_API_KEY as string) || '';

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.listOrganizations(undefined, undefined, 0, 100);
      setOrganizations(response.organizations);
      setError(null);
    } catch (err: unknown) {
      let errorMsg = 'Failed to fetch organizations';
      if (err instanceof Error) errorMsg = err.message;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    if (!platformApiKey) return;
    try {
      setInvoiceLoading(true);
      const response = await adminApi.listAllInvoices(platformApiKey, {
        status: invoiceStatusFilter || undefined,
        billingPeriod: invoicePeriodFilter || undefined,
        limit: 100,
      });
      setInvoices(response.invoices);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setInvoiceLoading(false);
    }
  }, [platformApiKey, invoiceStatusFilter, invoicePeriodFilter]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [invoiceStatusFilter, invoicePeriodFilter]);

  const handleSyncStripe = async () => {
    if (!platformApiKey) {
      setError('VITE_PLATFORM_API_KEY not configured');
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await adminApi.syncStripeInvoices(platformApiKey);
      setSyncResult(result);
      fetchInvoices();
    } catch (err: unknown) {
      let errorMsg = 'Stripe sync failed';
      if (err instanceof Error) errorMsg = err.message;
      setError(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  const handleReconciliation = async () => {
    if (!platformApiKey) {
      setError('VITE_PLATFORM_API_KEY not configured');
      return;
    }

    setReconciling(true);
    setReconReport(null);
    try {
      const report = await adminApi.getReconciliationReport(platformApiKey);
      setReconReport(report);
    } catch (err: unknown) {
      let errorMsg = 'Reconciliation failed';
      if (err instanceof Error) errorMsg = err.message;
      setError(errorMsg);
    } finally {
      setReconciling(false);
    }
  };

  const handleIssueCredit = async () => {
    if (!creditOrg || !creditAmount || !creditReason) return;

    const amountCents = Math.round(parseFloat(creditAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      setCreditError('Amount must be a positive number');
      return;
    }

    setCreditSubmitting(true);
    setCreditError(null);
    try {
      const result = await adminApi.issueCredit(creditOrg.id, amountCents, creditReason);
      if (result.success) {
        setCreditSuccess(`Credit of $${creditAmount} issued successfully. Transaction: ${result.transactionId}`);
        setCreditAmount('');
        setCreditReason('');
        setTimeout(() => {
          setCreditDialogOpen(false);
          setCreditSuccess(null);
          fetchOrganizations();
        }, 2000);
      }
    } catch (err: unknown) {
      let errorMsg = 'Failed to issue credit';
      if (err instanceof Error) errorMsg = err.message;
      setCreditError(errorMsg);
    } finally {
      setCreditSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const getStatusColor = (status: string): 'success' | 'danger' | 'brand' | 'informative' | 'subtle' => {
    switch (status) {
      case 'Paid': return 'success';
      case 'Failed':
      case 'Uncollectible': return 'danger';
      case 'Submitted': return 'brand';
      case 'Voided': return 'subtle';
      default: return 'informative';
    }
  };

  // Map org IDs to names for the invoice table
  const orgNameMap = organizations.reduce<Record<string, string>>((acc, org) => {
    acc[org.id] = org.displayName || org.name;
    return acc;
  }, {});

  // Filter to orgs with Stripe customers only (billable orgs)
  const billableOrgs = organizations.filter(org => org.stripeCustomerId);

  if (loading) {
    return (
      <div className="admin-loading">
        <Spinner size={32} label="Loading billing data..." />
      </div>
    );
  }

  return (
    <div className="organizations-tab">
      {error && (
        <div className="admin-error">
          <AlertTriangle />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-grid">
        <Card className="summary-card">
          <h3>Billable Organizations</h3>
          <p className="text-xs text-muted-foreground mt-0.5">With Stripe customers</p>
          <div className="summary-value">
            <DollarSign />
            <span>{billableOrgs.length}</span>
          </div>
        </Card>

        <Card className="summary-card">
          <h3>Total Monthly Spend</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Current month across all orgs</p>
          <div className="summary-value mrr">
            <DollarSign />
            <span>{formatCurrency(billableOrgs.reduce((sum, org) => sum + org.currentMonthSpend, 0))}</span>
          </div>
        </Card>

        <Card className="summary-card">
          <h3>Total AIEs</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Subscribed employees</p>
          <div className="summary-value">
            <span>{billableOrgs.reduce((sum, org) => sum + org.subscribedEmployeeCount, 0)}</span>
          </div>
        </Card>

        <Card className="summary-card">
          <h3>Internal Invoices</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Total in system</p>
          <div className="summary-value">
            <span>{invoices.length}</span>
          </div>
        </Card>
      </div>

      {/* Stripe Sync & Reconciliation */}
      <div className="subscriptions-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2>Invoice Operations</h2>
          <Button
            onClick={handleSyncStripe}
            disabled={syncing || !platformApiKey}
          >
            <RefreshCw />
            {syncing ? 'Syncing...' : 'Sync Stripe Invoices'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleReconciliation}
            disabled={reconciling || !platformApiKey}
          >
            <CheckCircle2 />
            {reconciling ? 'Checking...' : 'Run Reconciliation'}
          </Button>
        </div>

        {!platformApiKey && (
          <p className="text-sm text-destructive mb-3">
            Set VITE_PLATFORM_API_KEY environment variable to enable sync and reconciliation.
          </p>
        )}

        {syncResult && (
          <Card className="mb-4 p-3">
            <h3 className="text-base font-semibold text-foreground">Sync Results</h3>
            <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
              <span>Orgs scanned: <b>{syncResult.organizationsScanned}</b></span>
              <span>Created: <b>{syncResult.invoicesCreated}</b></span>
              <span>Skipped (already synced): <b>{syncResult.invoicesSkipped}</b></span>
              {syncResult.errors.length > 0 && (
                <span className="text-destructive">
                  Errors: <b>{syncResult.errors.length}</b>
                </span>
              )}
            </div>
            {syncResult.errors.length > 0 && (
              <div className="text-destructive" style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                {syncResult.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </Card>
        )}

        {reconReport && (
          <Card className="mb-4 p-3">
            <h3 className="text-base font-semibold text-foreground">Reconciliation Report</h3>
            <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
              <span>Orgs checked: <b>{reconReport.organizationsChecked}</b></span>
              <span>Flags: <b>{reconReport.flagsFound}</b></span>
            </div>
            {reconReport.flags.length > 0 ? (
              <Table style={{ marginTop: '12px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Severity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reconReport.flags.map((flag, i) => (
                    <TableRow key={i}>
                      <TableCell>{flag.organizationName || flag.organizationId}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={badgeClass('informative')}>{flag.flagType}</Badge>
                      </TableCell>
                      <TableCell style={{ fontSize: '0.85rem' }}>{flag.description}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={badgeClass(flag.severity === 'critical' ? 'danger' : flag.severity === 'warning' ? 'warning' : 'informative')}
                        >
                          {flag.severity}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="mt-2 text-accent-text">
                No reconciliation issues found.
              </p>
            )}
          </Card>
        )}
      </div>

      {/* Global Invoice Table */}
      <div className="subscriptions-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <h2>All Invoices</h2>
          <Button
            size="sm"
            onClick={() => setCreateInvoiceDialogOpen(true)}
            disabled={!platformApiKey}
          >
            <Plus />
            Create Invoice
          </Button>
          <Select
            value={invoiceStatusFilter || 'all'}
            onValueChange={(v) => setInvoiceStatusFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger aria-label="Filter by status" className="h-8 w-auto min-w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Finalized">Finalized</SelectItem>
              <SelectItem value="Submitted">Submitted</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Failed">Failed</SelectItem>
              <SelectItem value="Voided">Voided</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Period (yyyy-MM)"
            value={invoicePeriodFilter}
            onChange={(e) => setInvoicePeriodFilter(e.target.value)}
            className="h-8 w-[140px]"
          />
          {invoiceLoading && <Spinner size={16} />}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Stripe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map(inv => (
              <TableRow key={inv.id}>
                <TableCell style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{inv.number}</TableCell>
                <TableCell>{orgNameMap[inv.organizationId] || inv.organizationId}</TableCell>
                <TableCell>{inv.billingPeriod}</TableCell>
                <TableCell>{formatCurrency(inv.totalAmount)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={badgeClass(getStatusColor(inv.status))}>
                    {inv.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={badgeClass('informative')}>{inv.billingChannel}</Badge>
                </TableCell>
                <TableCell>
                  {inv.channelReferenceId && (
                    <a
                      href={`https://dashboard.stripe.com/invoices/${inv.channelReferenceId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'h-8 w-8')}
                      aria-label="Open invoice in Stripe"
                    >
                      <ExternalLink />
                    </a>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && !invoiceLoading && (
              <TableRow>
                <TableCell colSpan={7}>
                  {platformApiKey ? 'No invoices found. Run "Sync Stripe Invoices" to import.' : 'Configure VITE_PLATFORM_API_KEY to view invoices.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Organization Billing Table */}
      <div className="subscriptions-section">
        <h2>Organization Billing</h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organization</TableHead>
              <TableHead>Stripe Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>AIEs</TableHead>
              <TableHead>Current Spend</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {billableOrgs.map(org => (
              <TableRow
                key={org.id}
                className="clickable-row"
                onClick={() => { window.location.href = `/admin/orgs/${org.id}`; }}
                style={{ cursor: 'pointer' }}
              >
                <TableCell>{org.displayName || org.name}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  {org.stripeCustomerId && (
                    <a
                      href={`https://dashboard.stripe.com/customers/${org.stripeCustomerId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
                    >
                      {org.stripeCustomerId.substring(0, 18)}...
                      <ExternalLink />
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={badgeClass(org.status === 'Active' ? 'success' : org.status === 'Suspended' ? 'danger' : 'informative')}
                  >
                    {org.status}
                  </Badge>
                </TableCell>
                <TableCell>{org.subscribedEmployeeCount}</TableCell>
                <TableCell>{formatCurrency(org.currentMonthSpend)}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCreditOrg(org);
                      setCreditDialogOpen(true);
                      setCreditError(null);
                      setCreditSuccess(null);
                      setCreditAmount('');
                      setCreditReason('');
                    }}
                  >
                    <DollarSign />
                    Credit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {billableOrgs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>No billable organizations found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Invoice Dialog (global — needs org ID input) */}
      {createInvoiceDialogOpen && (
        <CreateInvoiceDialog
          hasStripe={false}
          onCreated={() => { setCreateInvoiceDialogOpen(false); fetchInvoices(); }}
          onClose={() => setCreateInvoiceDialogOpen(false)}
        />
      )}

      {/* Issue Credit Dialog */}
      <Modal open={creditDialogOpen} onOverlayClick={() => setCreditDialogOpen(false)}>
        <Modal.Content maxWidth="md" aria-labelledby="issue-credit-title">
          <Modal.Header className="px-6 py-4">
            <h2 id="issue-credit-title" className="text-lg font-semibold text-foreground">Issue Credit</h2>
          </Modal.Header>
          <Modal.Body className="px-6 py-4">
            {creditOrg && (
              <>
                <p style={{ marginBottom: '1rem' }}>
                  Issue a credit to <b>{creditOrg.displayName || creditOrg.name}</b> ({creditOrg.id})
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                    <Input
                      type="number"
                      className="pl-6"
                      placeholder="Amount in USD (e.g., 50.00)"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                    />
                  </div>
                  <Textarea
                    placeholder="Reason for credit..."
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    rows={3}
                  />
                </div>
                {creditError && (
                  <div className="text-destructive" style={{ marginTop: '8px' }}>
                    {creditError}
                  </div>
                )}
                {creditSuccess && (
                  <div className="text-accent-text" style={{ marginTop: '8px' }}>
                    {creditSuccess}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
            <Button variant="secondary" onClick={() => setCreditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleIssueCredit}
              disabled={!creditAmount || !creditReason || creditSubmitting}
            >
              {creditSubmitting ? 'Issuing...' : 'Issue Credit'}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </div>
  );
}

export default AdminBillingTab;
